import { cx } from "@suse-o11y/class-variance";
import {
	IconBell,
	IconComponents,
	IconDashboard,
	IconLogoStackState,
	IconMainMenuAnalytics,
	IconMainMenuCLI,
	IconMainMenuMetrics,
	IconMainMenuMonitors,
	IconMainMenuSettings,
	IconMainMenuStackpacks,
	IconMainMenuViews,
	IconTraces,
} from "@suse-o11y/icons";
import type { IdOrIdentifier } from "@suse-o11y/id-or-identifier";
import { isTruthy } from "@suse-o11y/is-truthy";
import { type Signal, useComputed } from "@suse-o11y/signal";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { SkeletonText } from "~/components/skeleton/text";
import { Truncated } from "~/components/truncated";
import { Type } from "~/models/dto";

import { selectTimeRange, selectTimestampDate } from "../../../app/state/view/timeline/TimelineSelectors";
import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import { useQueryViewBySlug } from "../../../common/use-query-view-by-slug";
import { DEFAULT_VIEW_TYPE } from "../../../hardcoded/view-types/DefaultViewType";
import { queryFullComponent, useQueryMonitor, useQueryNodeById, useQueryStackPacks } from "../../../queries";
import { queryDashboard } from "../../../queries/dashboards";
import { useActiveMainMenuViewItem, useQueryMainMenuGroups } from "../../../queries/mainmenu";
import { useNotificationConfiguration } from "../../../queries/notifications";
import { useQuery } from "../../../queries/react-query";
import { useGetViewTypeByIdentifier } from "../../../queries/viewtype";
import {
	ANALYTICS_PATH,
	CLIPROFILE_PATH,
	COMPONENT_PATH,
	DASHBOARDS_OVERVIEW_PATH,
	DASHBOARD_PATH,
	KUBERNETES_OVERVIEW_PATH,
	METRICS_PATH,
	MONITORS_PATH,
	NOTIFICATIONS_PATH,
	SETTINGS_PATH,
	STACKPACKS_INTEGRATION_PATH,
	STACKPACKS_PATH,
	STACKPACKS_STACKPACK_PATH,
	TRACES_PATH,
	VIEWS_OVERVIEW_PATH,
	VIEW_PATH,
	WELCOME_PATH,
} from "../../../routes/Routes";
import { DashboardScopeIcon } from "../../_dashboard-scope-icon";
import { useIsDropdownWithHostOpen } from "../../dropdown-with-host";
import { Link, useNavigateToLink } from "../link";
import { ComponentIcon } from "../states/ComponentIcon";

import type { SmartHistoryQueue } from "./SmartHistoryQueue";
import { type MonitorsPathParams, type NotificationsPathParams, schema } from "./schema";

export interface SmartHistoryProps {
	queue: Signal<SmartHistoryQueue>;
}

export const SmartHistory: React.FC<SmartHistoryProps> = ({ queue }) => {
	const queueValue = useComputed(queue);
	const timeRange = useSelector(selectTimeRange);
	const timestamp = useSelector(selectTimestampDate);
	const location = useLocation();
	const isParentDropdownOpen = useIsDropdownWithHostOpen();

	return (
		<>
			<header className="flex justify-between gap-4 px-3 py-4" data-testid="smart-history-header">
				<h5 className="m-0 font-medium text-grey-900 text-sm leading-4">Recent history</h5>

				<Link
					className="text-13"
					el="button"
					onClick={() => {
						queue.write((current) => current.clear().enqueue(location));
						isParentDropdownOpen?.write(false);
					}}
					theme="primary"
				>
					Clear
				</Link>
			</header>

			{/* the max-h value is approximately 10 items */}
			<ol className="m-0 max-h-[360px] list-none overflow-y-auto p-0 text-13 text-grey-800 leading-none">
				{queueValue
					.toArray(({ url, isActive }, index) => (
						<li key={index}>
							<SmartHistoryLink
								isActive={isActive}
								onClick={() => {
									queue.write((current) => current.jumpTo(index));
									isParentDropdownOpen?.write(false);
								}}
								timeRange={timeRange}
								timestamp={timestamp}
								url={url}
							/>
						</li>
					))
					// it is ok to mutate the array here because .toArray() creates a copy
					.reverse()}
			</ol>
		</>
	);
};

const SmartHistoryLink: React.FC<{
	url: string;
	timestamp: undefined | Date;
	timeRange: TimeRange;
	isActive: boolean;
	onClick: VoidFunction;
}> = ({ url, timestamp, timeRange, isActive, onClick }) => {
	const urlWithCurrentTimeRange = useNavigateToLink(url, {
		search: (search) => ({ ...search, timestamp, timeRange }),
	});

	return (
		<Link
			aria-selected={isActive}
			className={cx(
				"flex h-9 items-center gap-6 rounded-xs px-3 empty:hidden hover:bg-grey-200 focus-visible:bg-grey-200",
				isActive && "font-medium text-primary-600",
			)}
			data-testid="smart-history-entry"
			onClick={onClick}
			to={urlWithCurrentTimeRange}
		>
			<SmartHistoryEntry url={url} />
		</Link>
	);
};

const SmartHistoryEntry: React.FC<{
	url: string;
}> = ({ url }) =>
	schema.cata<React.ReactNode>(url, {
		[KUBERNETES_OVERVIEW_PATH]: () => <KubernetesOverviewEntry />,
		[VIEWS_OVERVIEW_PATH]: () => <EntryLayout fragments="Views" icon={<IconMainMenuViews />} />,
		[VIEW_PATH]: ({ slug, isExplore }) => <ViewEntry isExplore={isExplore} slug={slug} />,
		[DASHBOARDS_OVERVIEW_PATH]: () => <EntryLayout fragments="Dashboards" icon={<IconDashboard />} />,
		[DASHBOARD_PATH]: ({ idOrIdentifier }) => <DashboardEntry idOrIdentifier={idOrIdentifier} />,
		[COMPONENT_PATH]: ({ identifier, isExplore }) => <ComponentEntry identifier={identifier} isExplore={isExplore} />,
		[METRICS_PATH]: () => <EntryLayout fragments="Metrics" icon={<IconMainMenuMetrics />} />,
		[TRACES_PATH]: () => <EntryLayout fragments="Traces" icon={<IconTraces />} />,
		[MONITORS_PATH]: (params) => <MonitorsEntry params={params} />,
		[CLIPROFILE_PATH]: () => <EntryLayout fragments="CLI" icon={<IconMainMenuCLI />} />,
		[ANALYTICS_PATH]: () => <EntryLayout fragments="Analytics" icon={<IconMainMenuAnalytics />} />,
		[SETTINGS_PATH]: ({ name, id }) => (
			<EntryLayout
				fragments={name == null ? "Settings" : [name, id].filter(isTruthy)}
				icon={<IconMainMenuSettings />}
			/>
		),
		[NOTIFICATIONS_PATH]: (params) => <NotificationsEntry params={params} />,
		[STACKPACKS_PATH]: () => <StackPacksEntry />,
		[STACKPACKS_STACKPACK_PATH]: ({ name }) => <StackPacksEntry name={name} />,
		[STACKPACKS_INTEGRATION_PATH]: ({ name, integration }) => <StackPacksEntry integration={integration} name={name} />,
		[WELCOME_PATH]: () => <EntryLayout fragments="Home" icon={<IconLogoStackState />} />,
		_: () => null,
	});

const EntryLayout: React.FC<{
	icon: React.ReactElement;
	fragments: undefined | string | Array<string>;
}> = ({ icon, fragments }) => (
	<>
		{/* flex aligns the icon in the middle */}
		<div className="flex shrink-0 text-grey-800 text-lg">{icon}</div>
		<SkeletonText randomize when={fragments}>
			{(text) => <Truncated truncate="middle">{[text].flat().join(" / ")}</Truncated>}
		</SkeletonText>
	</>
);

const KubernetesOverviewEntry: React.FC = () => {
	const { isLoading, data: k8sGroup } = useQueryMainMenuGroups({
		select: (groups) => groups.find((group) => group.isKubernetes),
	});

	if (isLoading) {
		return <EntryLayout fragments={undefined} icon={<IconMainMenuViews />} />;
	}

	return (
		<EntryLayout
			fragments="Kubernetes"
			icon={k8sGroup?.icon ? <img alt="icon" className="size-[18px]" src={k8sGroup.icon} /> : <IconMainMenuViews />}
		/>
	);
};

const ViewEntry: React.FC<{
	slug: string;
	isExplore: boolean;
}> = ({ slug, isExplore }) => {
	const view = useQueryViewBySlug(slug)?.getOrElse(undefined);
	const menuItem = useActiveMainMenuViewItem(slug);

	const { data: viewType } = useGetViewTypeByIdentifier(view?.viewType);

	if (view === null) {
		return null;
	}

	return (
		<EntryLayout
			fragments={view ? [menuItem?.title ?? view.name ?? slug, isExplore && "Explore"].filter(isTruthy) : undefined}
			icon={
				viewType != null && viewType !== DEFAULT_VIEW_TYPE ? (
					<img alt="icon" className="size-[1em]" src={viewType.iconbase64} />
				) : (
					<IconMainMenuViews />
				)
			}
		/>
	);
};

const DashboardEntry: React.FC<{
	idOrIdentifier: IdOrIdentifier;
}> = ({ idOrIdentifier }) => {
	const { isLoading, data: dashboard } = useQuery(queryDashboard(idOrIdentifier));

	if (!isLoading && dashboard == null) {
		return null;
	}

	return <EntryLayout fragments={dashboard?.name} icon={<DashboardScopeIcon dashboardScope={dashboard?.scope} />} />;
};

function componentFragments(componentName: undefined | string, typeName: undefined | string, isExplore: boolean) {
	if (componentName == null) {
		return undefined;
	}

	const fullName = typeName ? `${componentName} (${typeName})` : componentName;

	return [fullName, isExplore && "Explore"].filter(isTruthy);
}

const ComponentEntry: React.FC<{
	identifier: string;
	isExplore: boolean;
}> = ({ identifier, isExplore }) => {
	const { isLoading, data: fullComponent } = useQuery({
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnTimelineSummaryUpdate: true,

		...queryFullComponent({ componentIdOrIdentifier: identifier, timestamp: undefined }),
	});

	const component = fullComponent?.data;
	const typeName = fullComponent?.typeName;

	if (!isLoading && component == null) {
		return null;
	}

	return (
		<EntryLayout
			fragments={componentFragments(component?.name, typeName, isExplore)}
			icon={fullComponent?.icon ? <ComponentIcon icon={fullComponent.icon} iconSize="tiny" /> : <IconComponents />}
		/>
	);
};

const NotificationsEntry: React.FC<{
	params: NotificationsPathParams;
}> = ({ params }) => {
	const { isLoading, data: notification } = useNotificationConfiguration(
		params.type === "edit" ? params.notificationId : "",
		{
			enabled: params.type === "edit",
		},
	);

	if (params.type === "overview") {
		return <EntryLayout fragments="Notifications" icon={<IconBell />} />;
	}

	if (params.type === "new") {
		return <EntryLayout fragments="Add new notification" icon={<IconBell />} />;
	}

	if (!isLoading && notification == null) {
		return null;
	}

	return <EntryLayout fragments={notification?.name} icon={<IconBell />} />;
};

const StackPacksEntry: React.FC<{
	name?: string;
	integration?: string;
}> = ({ name: nameSlug, integration: integrationSlug }) => {
	const { isLoading, data: stackPack } = useQueryStackPacks({
		select: (response) => response.find((item) => item.name === nameSlug),
		enabled: nameSlug != null,
	});

	if (nameSlug == null) {
		return <EntryLayout fragments="StackPacks" icon={<IconMainMenuStackpacks />} />;
	}

	if (isLoading) {
		return <EntryLayout fragments={undefined} icon={<IconMainMenuStackpacks />} />;
	}

	if (stackPack == null) {
		return null;
	}

	if (integrationSlug == null) {
		return <EntryLayout fragments={stackPack.displayName} icon={<IconMainMenuStackpacks />} />;
	}

	const integration = stackPack.integrations.find((item) => item.name === integrationSlug);

	return (
		<EntryLayout
			fragments={[stackPack.displayName, integration?.displayName ?? integrationSlug]}
			icon={<IconMainMenuStackpacks />}
		/>
	);
};

const MonitorsEntry: React.FC<{
	params: MonitorsPathParams;
}> = ({ params }) => {
	const { isLoading: isMonitorLoading, data: monitor } = useQueryMonitor(
		params.type === "edit" ? params.monitorId : "",
		{
			enabled: params.type === "edit",
			refetchOnWindowFocus: false,
		},
	);

	const { isLoading: isMonitorFunctionLoading, data: monitorFunction } = useQueryNodeById(
		Type.MonitorFunction,
		params.type === "new" ? params.monitorFunctionIdentifier : "",
		{
			enabled: params.type === "new",
			refetchOnWindowFocus: false,
		},
	);

	if (params.type === "overview") {
		return <EntryLayout fragments="Monitors" icon={<IconMainMenuMonitors />} />;
	}

	if (params.type === "edit") {
		return !isMonitorLoading && monitor == null ? null : (
			<EntryLayout fragments={monitor?.name} icon={<IconMainMenuMonitors />} />
		);
	}

	if (params.monitorFunctionIdentifier == null) {
		return <EntryLayout fragments="Add new monitor" icon={<IconMainMenuMonitors />} />;
	}

	if (isMonitorFunctionLoading) {
		return <EntryLayout fragments={undefined} icon={<IconMainMenuMonitors />} />;
	}

	if (monitorFunction == null) {
		return null;
	}

	return <EntryLayout fragments={["Add new monitor", monitorFunction.name]} icon={<IconMainMenuMonitors />} />;
};
